import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Input,
  Collapse,
} from 'reactstrap';
import { personnelAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import PersonnelFormModal from './PersonnelFormModal';
import PersonnelDetailModal from './PersonnelDetailModal';

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  tcNo: string | null;
  phone: string | null;
  title: string | null;
  dailyWage: number | null;
  isActive: boolean;
  status: string;
  statusNote: string | null;
  type: string;
  employmentType: string;
  monthlySalary: number | null;
  rating: number | null;
  comment: string | null;
  startDate: string | null;
}

const personnelStatusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  INACTIVE: 'Pasif',
  ON_LEAVE: 'İzinli',
  ON_REPORT: 'Raporlu',
};

const personnelStatusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  INACTIVE: 'bg-secondary bg-opacity-10 text-secondary',
  ON_LEAVE: 'bg-warning bg-opacity-10 text-warning',
  ON_REPORT: 'bg-info bg-opacity-10 text-info',
};

const PersonnelList: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Titles for filter dropdown
  const [titles, setTitles] = useState<string[]>([]);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);

  const openDetailModal = (person: Personnel, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedPersonnel(person);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedPersonnel(null);
  };

  useEffect(() => {
    fetchTitles();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchPersonnel();
  }, [titleFilter, statusFilter, debouncedSearch]);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      const params: { title?: string; status?: string; search?: string } = {};
      if (titleFilter) params.title = titleFilter;
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await personnelAPI.getAll(params);
      setPersonnel(response.data);
    } catch (error) {
      console.error('Personnel fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTitles = async () => {
    try {
      const response = await personnelAPI.getTitles();
      setTitles(response.data);
    } catch (error) {
      console.error('Titles fetch error:', error);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setTitleFilter('');
    setStatusFilter('');
  };

  const hasActiveFilters = searchQuery || titleFilter || statusFilter;

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Personel</h4>
            <div className="page-title-right">
              {isAdmin && (
                <Button color="primary" onClick={() => setFormModalOpen(true)}>
                  <i className="ri-add-line me-1"></i>
                  Yeni Personel
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">Personel Listesi</h5>
                </Col>
                {/* Desktop Filters */}
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Ad/Soyad Ara..."
                          style={{ minWidth: '160px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={titleFilter}
                          onChange={(e) => setTitleFilter(e.target.value)}
                          style={{ minWidth: '140px' }}
                        >
                          <option value="">Tüm Ünvanlar</option>
                          {titles.map((title) => (
                            <option key={title} value={title}>{title}</option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="ACTIVE">Aktif</option>
                          <option value="INACTIVE">Pasif</option>
                          <option value="ON_LEAVE">İzinli</option>
                          <option value="ON_REPORT">Raporlu</option>
                        </Input>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            title="Filtreleri Temizle"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[searchQuery, titleFilter, statusFilter].filter(Boolean).length}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ad/Soyad Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Ad veya soyad yazın..."
                        />
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ünvan</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={titleFilter}
                          onChange={(e) => setTitleFilter(e.target.value)}
                        >
                          <option value="">Tüm Ünvanlar</option>
                          {titles.map((title) => (
                            <option key={title} value={title}>{title}</option>
                          ))}
                        </Input>
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Durum</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">Tüm Durumlar</option>
                          <option value="ACTIVE">Aktif</option>
                          <option value="INACTIVE">Pasif</option>
                          <option value="ON_LEAVE">İzinli</option>
                          <option value="ON_REPORT">Raporlu</option>
                        </Input>
                      </div>

                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : personnel.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz personel kaydı bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {personnel.map((person) => (
                    <div key={person.id} className="admin-card-item">
                      {/* Card Header */}
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(person)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">
                          {person.firstName} {person.lastName}
                        </h6>
                        <span className={`admin-card-badge px-2 py-1 rounded-pill small ${personnelStatusStyles[person.status] || personnelStatusStyles.ACTIVE}`} style={{ fontWeight: 500 }}>
                          {personnelStatusLabels[person.status] || 'Aktif'}
                        </span>
                      </div>

                      {/* Card Body */}
                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(person)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-shield-user-line"></i>Ünvan
                          </span>
                          <span className="admin-info-value">
                            {person.title || 'İşçi'}
                          </span>
                        </div>

                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-phone-line"></i>Telefon
                          </span>
                          <span className="admin-info-value">
                            {person.phone || <span className="value-muted">-</span>}
                          </span>
                        </div>

                        {(person.dailyWage || person.monthlySalary) && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-money-dollar-circle-line"></i>
                              {person.employmentType === 'MONTHLY' ? 'Aylık Maaş' : 'Günlük Ücret'}
                            </span>
                            <div className="admin-info-value value-highlight">
                              {person.employmentType === 'MONTHLY'
                                ? `${person.monthlySalary?.toLocaleString('tr-TR')} TL`
                                : `${person.dailyWage?.toLocaleString('tr-TR')} TL`}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={(e) => openDetailModal(person, e)}
                        >
                          <i className="ri-eye-line"></i>
                          Detaylar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Ad Soyad</th>
                        <th>TC No</th>
                        <th>Telefon</th>
                        <th>Ünvan</th>
                        <th>Ücret</th>
                        <th>Başlangıç</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personnel.map((person) => (
                        <tr
                          key={person.id}
                          onClick={() => openDetailModal(person)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>
                              {person.firstName} {person.lastName}
                            </strong>
                          </td>
                          <td>{person.tcNo || '-'}</td>
                          <td>{person.phone || '-'}</td>
                          <td>{person.title || 'İşçi'}</td>
                          <td>
                            {person.employmentType === 'MONTHLY'
                              ? (person.monthlySalary ? `${person.monthlySalary.toLocaleString('tr-TR')} TL/ay` : '-')
                              : (person.dailyWage ? `${person.dailyWage.toLocaleString('tr-TR')} TL/gün` : '-')}
                          </td>
                          <td>
                            {person.startDate
                              ? new Date(person.startDate).toLocaleDateString('tr-TR')
                              : '-'}
                          </td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-pill small ${personnelStatusStyles[person.status] || personnelStatusStyles.ACTIVE}`}
                              style={{ fontWeight: 500 }}
                            >
                              {personnelStatusLabels[person.status] || 'Aktif'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni Personel Modal */}
      <PersonnelFormModal
        isOpen={formModalOpen}
        toggle={() => setFormModalOpen(false)}
        onSuccess={() => {
          fetchPersonnel();
          fetchTitles(); // Refresh titles in case a new one was added
        }}
      />

      {/* Personel Detay Modal */}
      <PersonnelDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        personnel={selectedPersonnel}
        onUpdate={() => {
          fetchPersonnel();
          fetchTitles();
        }}
      />
    </>
  );
};

export default PersonnelList;
